import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Download, Warehouse, Loader2, Power } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { MasterWarehouseForm } from './MasterWarehouseForm';

interface MasterWarehouse {
  id: number;
  name: string;
  code?: string;
  contactName?: string;
  contactPhone?: string;
  city?: string;
  state?: string;
  stateId?: number;
  pincode?: string;
  address?: string;
  rtoMobile?: string;
  rtoCity?: string;
  rtoState?: string;
  rtoStateId?: number;
  rtoPincode?: string;
  rtoAddress?: string;
  isActive: boolean;
}

export function MasterWarehousesPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<MasterWarehouse | null>(null);
  const [exporting, setExporting] = useState(false);

  const { data: rows, isLoading } = useQuery({
    queryKey: ['master-warehouses', search],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      return (await api.get<MasterWarehouse[]>('/master-warehouses', { params })).data;
    },
  });

  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/master-warehouses/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['master-warehouses'] }); toast.success('Status updated'); },
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get('/master-warehouses/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `master-warehouses-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Master Warehouses</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Shared pickup locations available to <strong>all merchants</strong> during order booking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2 disabled:opacity-60">
            <Download size={14} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button onClick={() => setShowCreate(true)}
            className="bg-primary text-white px-3 py-2 text-sm rounded-lg font-medium hover:bg-primary-700 transition flex items-center gap-2">
            <Plus size={14} /> Add Master Warehouse
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50/50">
          <div className="flex-1 flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-slate-200">
            <Search size={14} className="text-slate-400" />
            <input type="text" placeholder="Search by name, code, city..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Master Warehouse</th>
                <th className="text-left px-4 py-3">City / State</th>
                <th className="text-left px-4 py-3">Pincode</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">RTO</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Loading...</td></tr>}
              {!isLoading && (rows?.length ?? 0) === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-400">
                  <Warehouse size={20} className="mx-auto mb-1 opacity-50" />
                  <p className="text-xs">No master warehouses yet. Add the first shared pickup location.</p>
                </td></tr>
              )}
              {rows?.map((w) => (
                <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs">#{w.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{w.name}</div>
                    {w.code && <div className="text-xs"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{w.code}</code></div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{[w.city, w.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs font-mono">{w.pincode ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {w.contactName ?? '—'}{w.contactPhone && <div className="text-slate-400">{w.contactPhone}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">
                    {w.rtoCity || w.rtoMobile
                      ? <>{[w.rtoCity, w.rtoState, w.rtoPincode].filter(Boolean).join(', ')}{w.rtoMobile && <div className="text-slate-400">{w.rtoMobile}</div>}</>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${w.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${w.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {w.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(w)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit"><Edit2 size={14} /></button>
                      <button onClick={() => toggle.mutate(w.id)}
                        className={`p-1.5 rounded ${w.isActive ? 'text-amber-600 hover:bg-amber-50' : 'text-success hover:bg-success/10'}`}
                        title={w.isActive ? 'Deactivate' : 'Activate'}>
                        {toggle.isPending ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)}
        title="Add Master Warehouse" subtitle="Shared pickup location available to all merchants" maxWidth="3xl">
        <MasterWarehouseForm onSuccess={() => setShowCreate(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title="Edit Master Warehouse" subtitle={editing?.name} maxWidth="3xl">
        {editing && <MasterWarehouseForm warehouseId={editing.id} defaultValues={editing} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}
